import NavigationWrapper from "@/src/components/ui-kit/nav-bar/navigation-wrapper";
import { useTheme } from "@/src/styles/theme-provider";
import { Stack, usePathname, useRouter } from "expo-router";
import React, { useCallback, useMemo } from "react";
import { Platform, View, useWindowDimensions } from "react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";

// Main tabs in swipe order (matches the bottom nav). Swipe is only active on
// these exact roots — never on detail screens — so reading a post/article and
// swiping doesn't jump tabs.
const SWIPE_TAB_PATHS = [
  "/feeds",
  "/news/latest-news",
  "/events",
  "/groups",
] as const;

export default function TabsLayout() {
  const { width: windowWidth } = useWindowDimensions();
  const isMobile = windowWidth < 768;
  const pathname = usePathname();
  const router = useRouter();
  const { themeVars } = useTheme();

  // Theme the Stack's screen background so push/pop transitions never flash a
  // default white screen (jarring in dark mode).
  const stackScreenOptions = {
    headerShown: false,
    // No per-screen transition: tab switches swap instantly (the swipe supplies
    // its own motion). Prevents the previous screen flashing at the edges/padding
    // during the native-stack slide.
    animation: "none",
    contentStyle: {
      backgroundColor: themeVars["--color-obsidian-default"] as string,
    },
  } as const;

  const gap =
    windowWidth >= 1280
      ? "gap-14"
      : windowWidth >= 1024
        ? "gap-10"
        : windowWidth >= 768
          ? "gap-7"
          : "gap-5";

  const navigateByOffset = useCallback(
    (offset: number) => {
      const current = SWIPE_TAB_PATHS.indexOf(
        pathname as (typeof SWIPE_TAB_PATHS)[number],
      );
      if (current === -1) return;
      const next = current + offset;
      if (next < 0 || next >= SWIPE_TAB_PATHS.length) return;
      router.navigate(SWIPE_TAB_PATHS[next]);
    },
    [pathname, router],
  );

  // The current screen follows the finger (revealing the themed background).
  // Past 30% of the width the tab changes; otherwise it eases back. No bounce.
  const translateX = useSharedValue(0);

  const currentIndex = SWIPE_TAB_PATHS.indexOf(
    pathname as (typeof SWIPE_TAB_PATHS)[number],
  );
  const canGoNext =
    currentIndex !== -1 && currentIndex < SWIPE_TAB_PATHS.length - 1;
  const canGoPrev = currentIndex > 0;

  const swipeGesture = useMemo(
    () =>
      Gesture.Pan()
        .activeOffsetX([-20, 20])
        .failOffsetY([-15, 15])
        .onUpdate((event) => {
          if (currentIndex === -1) return;
          let tx = event.translationX;
          // Rubber-band when there is no tab in the dragged direction.
          if ((tx > 0 && !canGoPrev) || (tx < 0 && !canGoNext)) tx *= 0.25;
          translateX.value = tx;
        })
        .onEnd((event) => {
          const threshold = windowWidth * 0.3;
          if (event.translationX <= -threshold && canGoNext) {
            runOnJS(navigateByOffset)(1);
            translateX.value = withTiming(0, { duration: 200 });
          } else if (event.translationX >= threshold && canGoPrev) {
            runOnJS(navigateByOffset)(-1);
            translateX.value = withTiming(0, { duration: 200 });
          } else {
            // Below threshold → ease back to centre (no bounce, no tab change).
            translateX.value = withTiming(0, { duration: 180 });
          }
        }),
    [navigateByOffset, currentIndex, canGoNext, canGoPrev, windowWidth, translateX],
  );

  const swipeAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
  }));

  if (isMobile) {
    // The finger-following swipe is a touch interaction. On web (where the phone
    // layout also renders at narrow widths) the Pan gesture captures pointer
    // events and the reanimated transform wraps every screen, breaking scroll
    // and interaction — so web gets the same layout without the gesture wrapper.
    if (Platform.OS === "web") {
      return (
        <View className="flex-1 bg-obsidian">
          <View className="flex-1 pb-16 px-4 pt-3">
            <Stack screenOptions={stackScreenOptions} />
          </View>
          <NavigationWrapper />
        </View>
      );
    }

    return (
      <View className="flex-1 bg-obsidian">
        <GestureDetector gesture={swipeGesture}>
          <Animated.View
            className="flex-1 pb-16 px-4 pt-3"
            style={swipeAnimatedStyle}
          >
            <Stack screenOptions={stackScreenOptions} />
          </Animated.View>
        </GestureDetector>
        <NavigationWrapper />
      </View>
    );
  }

  return (
    <View className={`flex-row flex-1 p-4 bg-obsidian ${gap}`}>
      <NavigationWrapper />
      <View className="flex-1">
        <Stack screenOptions={{ headerShown: false }} />
      </View>
    </View>
  );
}
